import { Component, computed, inject, signal } from '@angular/core';
import { PermissionService } from '../../shared/data-access/permission.service';
import { ActiveRoleService } from '../../shared/data-access/active-role.service';
import { ROLE_LABELS } from '../../shared/data-access/user-role.types';
import { PermissionCode } from '../../shared/data-access/permission.types';
import { RoleMenuConfigService } from '../../shared/data-access/role-menu-config.service';
import { IconComponent, SideNavPanelComponent, SideNavItem } from '../../shared/ui';

/** Storage key for the desktop collapsed/expanded preference (survives page refresh). */
const COLLAPSE_STORAGE_KEY = 'mc-sidebar-collapsed';

interface CommonMenuEntry {
  readonly key: string;
  readonly label: string;
  readonly icon: SideNavItem['icon'];
  readonly route: string;
  readonly requiredRight: PermissionCode;
}

/** Navigation shared by every role — always the first section of the sidebar. */
const COMMON_MENU: readonly CommonMenuEntry[] = [
  { key: '/dashboard', label: 'Dashboard', icon: 'home',   route: '/dashboard', requiredRight: 'DASHBOARD_VIEW' },
  { key: '/dossiers',  label: 'Dossiers',  icon: 'folder', route: '/dossiers',  requiredRight: 'CASE_VIEW' },
  { key: '/clients',   label: 'Clients',   icon: 'users',  route: '/clients',   requiredRight: 'CLIENT_VIEW' },
];

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

@Component({
  selector: 'mc-app-sidebar',
  standalone: true,
  imports: [IconComponent, SideNavPanelComponent],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  host: {
    '[class.sidebar--collapsed]': 'collapsed()',
  },
})
export class AppSidebarComponent {
  private readonly activeRole   = inject(ActiveRoleService);
  private readonly permissions  = inject(PermissionService);
  private readonly menuConfig   = inject(RoleMenuConfigService);

  protected readonly user         = this.activeRole.currentUser;
  protected readonly roleLabel    = computed(() => ROLE_LABELS[this.activeRole.currentRole()]);
  protected readonly sectionTitle = computed(() =>
    this.menuConfig.getSectionTitle(this.activeRole.currentRole()),
  );

  /** Desktop icon-only rail mode. Irrelevant on mobile/tablet, where the sidebar is a drawer. */
  protected readonly collapsed = signal(readStoredCollapsed());

  /** Common items (Dashboard/Dossiers/Clients), filtered by the user's current rights. */
  protected readonly commonMenu = computed<SideNavItem[]>(() =>
    COMMON_MENU
      .filter(item => this.permissions.hasRight(item.requiredRight))
      .map(item => ({ key: item.key, label: item.label, icon: item.icon, route: item.route })),
  );

  /** Contextual items for the active role, filtered by the user's current rights. */
  protected readonly contextualMenu = computed<SideNavItem[]>(() => {
    const items = this.menuConfig.getContextualItems(this.activeRole.currentRole());
    return items
      .filter(item => this.permissions.hasRight(item.requiredRight))
      .map(item => ({
        key:         item.route,
        label:       item.label,
        icon:        item.icon,
        route:       item.route,
        queryParams: item.queryParams,
        exactMatch:  item.exactMatch,
      }));
  });

  protected toggleCollapsed(): void {
    const next = !this.collapsed();
    this.collapsed.set(next);
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      // Storage unavailable (private browsing, etc.) — collapse state simply won't persist.
    }
  }
}
